import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ComparativoPropostaVisual } from '@/components/leads-carteira/ComparativoPropostaVisual';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { 
  getPlanoById, 
  calcularTotalGB, 
  calcularTotalValor,
  type ComposicaoProposta,
  type LinhaComposicao,
} from '@/constants/planosCNPJ';
import { useResolverPlanoCatalogo } from '@/hooks/usePlanosPropostaCatalogo';
import { RenovacaoSnapshotPedido } from './RenovacaoSnapshotPedido';

interface Props {
  leadOrigemId: string;
  pedidoId: string;
}

function EnviosPreservados({ leadId, pedidoId }: { leadId: string; pedidoId: string }) {
  const { data: envios = [], isLoading, isError } = useQuery({
    queryKey: ['proposta-envios-preservados', leadId, pedidoId],
    queryFn: async () => {
      const { data: registros, error } = await supabase.from('leads_carteira_historico')
        .select('id,dados,created_at').eq('lead_id', leadId)
        .eq('acao', 'ENVIO_COMERCIAL_PREPARADO').order('created_at', { ascending: false }).limit(20);
      if (error) throw error;
      type Registro = { id: string; dados: {
        telefone_destino?: string; message_source?: string; composicao?: ComposicaoProposta;
        pedido?: { id: string; status: string; itens: unknown[]; email: string };
        mensagens?: Array<{ id: string; conversation_id: string }>;
      } };
      const lista = ((registros || []) as unknown as Registro[]).filter(r => !r.dados?.pedido || r.dados.pedido.id === pedidoId);
      const ids = [...new Set(lista.flatMap(r => (r.dados?.mensagens || []).map(m => m.id)))];
      if (!ids.length) return [];
      const { data: mensagens, error: erroMensagens } = await supabase.from('whatsapp_messages')
        .select('id,body,status,provider_message_id,conversation_id,created_at')
        .in('id', ids).eq('direction', 'outbound').eq('is_private_note', false)
        .in('status', ['sent', 'delivered', 'read']);
      if (erroMensagens) throw erroMensagens;
      return lista.flatMap(registro => {
        const mensagem = mensagens?.find(m => m.provider_message_id
          && registro.dados?.mensagens?.some(v => v.id === m.id && v.conversation_id === m.conversation_id));
        return mensagem ? [{ ...registro, mensagem }] : [];
      });
    },
    enabled: !!leadId,
    refetchOnMount: 'always',
  });
  return (
    <div className="mb-4 space-y-2 rounded-md border p-3">
      <h4 className="text-sm font-medium">Envios com versão preservada</h4>
      {isLoading ? <p className="text-sm text-muted-foreground">Conferindo registros de envio…</p>
        : isError ? <p className="text-sm text-destructive">Não foi possível consultar os registros. Isso não significa que a proposta não foi enviada.</p>
        : !envios.length ? <p className="text-sm text-muted-foreground">Nenhuma versão preservada disponível. Envios antigos continuam no Inbox; este aviso não significa ausência de proposta.</p>
        : envios.map(({ id, dados, mensagem }) => (
          <details key={id} className="rounded border p-2 text-sm">
            <summary className="cursor-pointer">
              {dados.pedido ? dados.pedido.status : dados.message_source === 'fechamento_lead' ? 'Fechamento' : 'Proposta'} · {dados.telefone_destino} · {new Date(mensagem.created_at).toLocaleString('pt-BR')}
            </summary>
            <p className="mt-2 text-xs text-muted-foreground">
              Mensagem registrada no envio. A composição abaixo foi preservada naquele momento e não é recalculada pelo catálogo atual.
            </p>
            <p className="my-2 whitespace-pre-wrap">{mensagem.body || 'Envio de mídia — consulte a mensagem no Inbox.'}</p>
            {dados.pedido ? <details className="my-2"><summary>Itens do pedido preservados no envio</summary><pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify(dados.pedido, null, 2)}</pre></details> : dados.composicao?.linhas?.length ? (
              <details className="my-2">
                <summary className="cursor-pointer">Composição salva — {dados.composicao.linhas.length} item(ns)</summary>
                <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">{JSON.stringify(dados.composicao, null, 2)}</pre>
              </details>
            ) : <p className="text-xs text-muted-foreground">Composição estruturada indisponível neste registro.</p>}
            <a className="mt-2 inline-block underline" href={`/whatsapp-inbox?conversation=${mensagem.conversation_id}`}>Conferir no Inbox</a>
          </details>
        ))}
    </div>
  );
}

export function PropostaOrigemPedido({ leadOrigemId, pedidoId }: Props) {
  const navigate = useNavigate();
  const painelPedido = usePainelPedido();
  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead-proposta-origem', leadOrigemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads_carteira')
        .select('id, razao_social, proposta_composicao_cnpj, valor_fatura_atual, tipo_lead, tipo_pessoa, e_cliente_carteira')
        .eq('id', leadOrigemId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!leadOrigemId,
  });
  const resolverPlano = useResolverPlanoCatalogo((lead as { tipo_pessoa?: string | null } | undefined)?.tipo_pessoa === 'PF' ? 'PF' : 'PJ');

  // ✅ CORREÇÃO: useMemo ANTES de qualquer return condicional (regra de hooks)
  const composicaoHidratada = useMemo((): ComposicaoProposta | null => {
    if (!lead?.proposta_composicao_cnpj) return null;
    
    const composicaoSalva = lead.proposta_composicao_cnpj as unknown as ComposicaoProposta;
    if (!Array.isArray(composicaoSalva.linhas)) return null;
    
    // Hidratar linhas com objetos plano completos
    const linhasHidratadas: LinhaComposicao[] = composicaoSalva.linhas.map((l) => ({
      ...l,
      plano: l.planoId ? resolverPlano(l.planoId, l.tipo) : undefined,
      adicionais: l.adicionais || undefined,
    }));
    
    const temFibra = linhasHidratadas.some(l => l.tipo === 'FIBRA');
    const planoFibra = linhasHidratadas.find(l => l.tipo === 'FIBRA')?.plano;
    
    return {
      linhas: linhasHidratadas,
      incluiFibra: temFibra,
      planoFibra,
      totalGB: calcularTotalGB(linhasHidratadas, temFibra),
      totalValor: calcularTotalValor(linhasHidratadas),
      economiaValor: 0,
      economiaPercent: 0,
    };
  }, [lead?.proposta_composicao_cnpj, resolverPlano]);

  // O comparativo fechado da renovação segue o PRODUTO da proposta, não o tipo
  // do lead (Douglas 06/09/2026): desde 04/09 o banco aceita renovação por
  // linha em lead MANUAL, e a régua antiga (`tipo_lead === 'RENOVACAO_CLARO'`)
  // esconderia o Delta de um pedido que renovou de verdade.
  const propostaTemRenovacao = (composicaoHidratada?.linhas || []).some((linha) => linha.tipo === 'RENOVACAO');

  // ✅ Early returns DEPOIS de todos os hooks
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!lead?.proposta_composicao_cnpj) {
    return (
      <div className="space-y-3">
        <EnviosPreservados leadId={leadOrigemId} pedidoId={pedidoId} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Proposta não encontrada para este lead</p>
          </CardContent>
        </Card>
        {/* Sem composição salva não há produto para ler — só o lead RENOVACAO_CLARO
            responde pelas renovações legadas (14 pedidos), e este bloco existe
            para elas. Onde há composição, quem manda é a linha de RENOVAÇÃO. */}
        {lead?.tipo_lead === 'RENOVACAO_CLARO' && (
          <RenovacaoSnapshotPedido pedidoId={pedidoId} leadId={leadOrigemId} />
        )}
      </div>
    );
  }

  // Determinar origem do lead
  const origemLabel = lead.tipo_lead === 'TAB' 
    ? 'Tabulação (Discadora)' 
    : lead.e_cliente_carteira 
      ? 'Carteira de Clientes' 
      : 'Prospecção';

  // Montar plano atual para o comparativo (usando valor_fatura_atual)
  const planoAtual = {
    linhas: [],
    valor: lead.valor_fatura_atual || 0,
    tipo: 'COMBO' as const,
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Proposta atual do lead
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {lead.razao_social} • {origemLabel}
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            // ✅ Fechar o Painel do Pedido antes de navegar
            painelPedido.close();
            // Navegar para o lead com tab correta
            navigate(`/comercial?tab=prospeccao&lead=${lead.id}`);
          }}
          className="gap-2"
        >
          <ExternalLink className="h-4 w-4" />
          Ver Lead
        </Button>
      </CardHeader>
      <CardContent>
        <EnviosPreservados leadId={leadOrigemId} pedidoId={pedidoId} />
        {composicaoHidratada ? (
          <ComparativoPropostaVisual
            planoAtual={planoAtual}
            composicaoNova={composicaoHidratada}
            ocultarPlanoAtual
          />
        ) : (
          <p className="text-center text-muted-foreground">Erro ao carregar composição</p>
        )}
      </CardContent>
      {propostaTemRenovacao && (
        <CardContent className="pt-0">
          <RenovacaoSnapshotPedido pedidoId={pedidoId} leadId={leadOrigemId} />
        </CardContent>
      )}
    </Card>
  );
}
